/**
 * useEncryptedSearch - Client-side search for encrypted conversations
 *
 * Since the server cannot search encrypted content, this hook loads
 * conversations client-side and searches through decrypted content.
 *
 * For performance:
 * - Only loads conversations when search is initiated
 * - Caches decrypted content
 * - Limits search to most recent conversations
 */
import { useState, useCallback, useRef } from 'react';
import { useCrypto } from './useCrypto';

export interface SearchableConversation {
  conversationId: string;
  title: string;
  encrypted: boolean;
  decryptedTitle?: string;
  updatedAt: string;
  favorite: boolean;
}

export interface SearchResult {
  conversationId: string;
  title: string;
  matchedText?: string;
  favorite: boolean;
  updatedAt: string;
}

interface UseEncryptedSearchOptions {
  maxResults?: number;
}

export function useEncryptedSearch(
  conversations: SearchableConversation[],
  options: UseEncryptedSearchOptions = {}
) {
  const { maxResults = 20 } = options;
  const { isUnlocked, decrypt } = useCrypto();

  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Cache for decrypted titles
  const decryptedCache = useRef<Map<string, string>>(new Map());

  // Decrypt a title if needed
  const decryptTitle = useCallback(
    async (conv: SearchableConversation): Promise<string> => {
      // Return cached if available
      if (decryptedCache.current.has(conv.conversationId)) {
        return decryptedCache.current.get(conv.conversationId)!;
      }

      // Return as-is if not encrypted
      if (!conv.encrypted) {
        return conv.title;
      }

      // Decrypt and cache
      if (!isUnlocked) {
        throw new Error('Cannot decrypt - vault is locked');
      }

      try {
        const decrypted = await decrypt(conv.title);
        decryptedCache.current.set(conv.conversationId, decrypted);
        return decrypted;
      } catch (e) {
        console.warn(`Failed to decrypt title for ${conv.conversationId}:`, e);
        return '[Encrypted]';
      }
    },
    [isUnlocked, decrypt]
  );

  // Search through conversations
  const search = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setResults([]);
        return [];
      }

      setIsSearching(true);
      setError(null);

      try {
        const normalizedQuery = query.toLowerCase().trim();
        const searchResults: SearchResult[] = [];

        // Search through all conversations
        for (const conv of conversations) {
          const title = await decryptTitle(conv);
          const titleLower = title.toLowerCase();

          // Check if title matches query
          if (titleLower.includes(normalizedQuery)) {
            searchResults.push({
              conversationId: conv.conversationId,
              title,
              matchedText: title,
              favorite: conv.favorite,
              updatedAt: conv.updatedAt,
            });

            if (searchResults.length >= maxResults) {
              break;
            }
          }
        }

        // Sort by most recent first
        searchResults.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );

        setResults(searchResults);
        return searchResults;
      } catch (e) {
        const errorMsg = e instanceof Error ? e.message : 'Search failed';
        setError(errorMsg);
        setResults([]);
        return [];
      } finally {
        setIsSearching(false);
      }
    },
    [conversations, decryptTitle, maxResults]
  );

  // Clear cache when conversations change significantly
  const clearCache = useCallback(() => {
    decryptedCache.current.clear();
  }, []);

  // Pre-decrypt visible conversations for faster search
  const preloadDecryption = useCallback(
    async (convIds: string[]) => {
      if (!isUnlocked) return;

      for (const id of convIds) {
        const conv = conversations.find((c) => c.conversationId === id);
        if (conv && conv.encrypted && !decryptedCache.current.has(id)) {
          try {
            await decryptTitle(conv);
          } catch {
            // Ignore errors during preload
          }
        }
      }
    },
    [conversations, decryptTitle, isUnlocked]
  );

  return {
    search,
    results,
    isSearching,
    error,
    clearCache,
    preloadDecryption,
    isEncryptionEnabled: isUnlocked,
  };
}
